import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import { useDebounce } from "hooks/useDebounce";
import { useCallback, useMemo, useState } from "react";

export type OmnisearchColumnFilterMenuProps = {
  columns: GridColDef[];
  handleToggleColumn: (column: string) => void;
  selectedColumns: GridColumnVisibilityModel;
  setSelectedColumns: (columns: GridColumnVisibilityModel) => void;
};

export const OmnisearchColumnFilterMenu = (
  props: OmnisearchColumnFilterMenuProps
) => {
  const { columns, handleToggleColumn, selectedColumns, setSelectedColumns } =
    props;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 150);

  const filteredColumns = useMemo(() => {
    if (!debouncedSearch) return columns;
    const query = debouncedSearch.toLowerCase();
    return columns.filter((column) => {
      const searchableText = [column.headerName, column.field]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [columns, debouncedSearch]);

  const isColumnVisible = useCallback(
    ({ field, hideable }: GridColDef) => {
      if (hideable === false) return true;
      if (!selectedColumns) return true;
      if (!(field in selectedColumns)) return true;
      return selectedColumns[field] !== false;
    },
    [selectedColumns]
  );

  const allSelected = useMemo(() => {
    if (!selectedColumns) return true;
    return filteredColumns.every(isColumnVisible);
  }, [filteredColumns, isColumnVisible, selectedColumns]);

  const indeterminate = useMemo(() => {
    if (!selectedColumns) return false;
    const hasVisible = filteredColumns.some(isColumnVisible);
    const hasHidden = filteredColumns.some(
      (col) => selectedColumns[col.field] === false
    );
    return hasVisible && hasHidden;
  }, [selectedColumns, filteredColumns, isColumnVisible]);

  const handleToggleAll = useCallback(() => {
    const newValue = !allSelected;
    const newModel = { ...selectedColumns };
    filteredColumns.forEach((col) => {
      if (col.hideable !== false) {
        newModel[col.field] = newValue;
      }
    });
    setSelectedColumns(newModel);
  }, [filteredColumns, allSelected, setSelectedColumns, selectedColumns]);

  const handleReset = useCallback(() => {
    setSelectedColumns({});
  }, [setSelectedColumns]);

  const areAllColumnsVisible = useMemo(() => {
    if (!selectedColumns) return true;
    return columns.every(
      (col) => col.hideable === false || selectedColumns[col.field] !== false
    );
  }, [columns, selectedColumns]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "250px",
        maxHeight: "400px",
      }}
    >
      <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          size="small"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {debouncedSearch && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, py: 0.5, display: "block" }}
          >
            Showing {filteredColumns.length} of {columns.length} columns
          </Typography>
        )}
        {filteredColumns.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 1, display: "block" }}
          >
            No columns match your search
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {filteredColumns.map((column) => (
              <ListItem
                key={column.field}
                sx={{
                  p: 0,
                  pl: 1,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedColumns?.[column.field] !== false}
                      onChange={() => handleToggleColumn(column.field)}
                      disabled={column.hideable === false}
                      size="small"
                    />
                  }
                  label={column.headerName || column.field}
                  sx={{
                    width: "100%",
                    margin: 0,
                    "& .MuiFormControlLabel-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <Box
        sx={{
          p: 1,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={handleToggleAll}
              size="small"
            />
          }
          label="Show/Hide All"
          sx={{
            margin: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              color: "text.secondary",
            },
          }}
        />
        <Button
          disabled={areAllColumnsVisible}
          onClick={handleReset}
          size="small"
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
};

export type OmnisearchColumnPopoverProps = {
  showColumnList: boolean;
  anchorEl: HTMLElement | null;
  handleCloseColumnList: () => void;
  columns: GridColDef[];
  handleToggleColumn: (column: string) => void;
  selectedColumns: GridColumnVisibilityModel;
  setSelectedColumns: (columns: GridColumnVisibilityModel) => void;
};

export const OmnisearchColumnPopover = (
  props: OmnisearchColumnPopoverProps
) => {
  const {
    showColumnList,
    anchorEl,
    handleCloseColumnList,
    columns,
    handleToggleColumn,
    selectedColumns,
    setSelectedColumns,
  } = props;

  return (
    <Popover
      open={showColumnList}
      anchorEl={anchorEl}
      onClose={handleCloseColumnList}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <OmnisearchColumnFilterMenu
        columns={columns}
        handleToggleColumn={handleToggleColumn}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
      />
    </Popover>
  );
};
